/*!
  Pioneer WFRIA DateRangePicker v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["DateRangePicker"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("DateRangePicker") < 0) {
    window.__PIONEER_GLOBAL__.components.push("DateRangePicker");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import PropTypes from "prop-types";
import React from "react";
import { isDate } from "../DatePicker/DateUtils";
import { getDateFormat, getValidConvertedDate } from "../DatePicker/utils";
import Flexgrid from "../Flexgrid/Flexgrid";
import FlexgridItem from "../Flexgrid/FlexgridItem";
/**
 * DateRangePicker is a React component that renders two DatePickers.
 * Its main function is to manage the selectability of calendar dates based on
 * the dates that are entered or selected in either field.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function DateRangePicker(_ref) {
  let {
    toDatePicker,
    fromDatePicker
  } = _ref;
  const {
    dateFormat
  } = fromDatePicker === null || fromDatePicker === void 0 ? void 0 : fromDatePicker.props;
  const dateFormatter = getDateFormat(dateFormat);
  /**
   * @description Calculates the minSelectableDate of the Datepicker that contains the 'To' date
   * based on the selected date in the Datepicker that contains the 'From' date so that the user
   * cannot choose a date that is out of the possible logical choices.
   * @param {enteredValue} The entered value in the input as a Date Object.
   * @returns {Object} The Date Object to be used as the minSelectableDate.
   */

  const getMinSelectableDate = fromDate => {
    const {
      minSelectableDate,
      maxSelectableDate
    } = toDatePicker.props;

    if (fromDate && isDate(fromDate)) {
      let newMinSelectableDate = Math.max(minSelectableDate.getTime(), fromDate.getTime());
      /**
       * This reverts to the passed in value for minSelectable date so that our minSelectableDate will
       * not ever be greater than the maxSelectableDate. If we pass in a value that is greater
       * than minSelectableDate it can result in future dates becoming enabled incorrectly. This could
       * occur if a user manually enters an invalid date into the "from" Datepicker.
       * i.e. given the following props
       *    maxSelectableDate === 9/15/2020
       *    minSelectableDate === 9/01/2020
       *    fromDate          === 9/20/2020
       * then minSelectableDate should equal the same date as maxSelectableDate.
       */

      if (newMinSelectableDate > maxSelectableDate.getTime()) {
        newMinSelectableDate = minSelectableDate;
      }

      return new Date(newMinSelectableDate);
    } else {
      return minSelectableDate;
    }
  };
  /**
   * @description Calculates the maxSelectableDate of the Datepicker that contains the 'From' date
   * based on the selected date in the Datepicker that contains the 'To' date so that the user
   * cannot choose a date that is out of the possible logical choices.
   * @param {enteredValue} The entered value in the input as a Date Object.
   * @returns {Object} The Date Object to be used as the minSelectableDate.
   */


  const getMaxSelectableDate = toDate => {
    const passedMaxSelectableDate = fromDatePicker.props.maxSelectableDate;

    if (toDate && isDate(toDate)) {
      const newMaxSelectableDate = Math.min(passedMaxSelectableDate.getTime(), toDate.getTime());
      return new Date(newMaxSelectableDate);
    } else {
      return passedMaxSelectableDate;
    }
  };
  /**
   * @description Retrieves the string values entered into both datepickers and
   *              sends them to the selectedDates prop
   * @returns {Object} An Object containing two keys, 'from' and 'to' consisting
   *                   of the corresponding datepicker string values
   */


  const getNewSelectedDates = () => {
    const toValue = toDatePicker.props.value;
    const fromValue = fromDatePicker.props.value;
    const toDate = toValue ? getValidConvertedDate(toValue, dateFormatter) : null;
    const fromDate = fromValue ? getValidConvertedDate(fromValue, dateFormatter) : null; // If we don't have both dates, don't highlight a range.

    if (!toDate || !isDate(toDate) || !fromDate || !isDate(fromDate)) {
      return;
    } // If fromDate is after toDate it's not a valid range, don't highlight a range.


    if (fromDate.getTime() > toDate.getTime()) {
      return;
    } // Return the string values for Datepicker to use.


    return {
      from: fromValue,
      to: toValue
    };
  };

  const {
    maxSelectableDate: maxFromDate,
    minSelectableDate: minFromDate,
    value: fromValue
  } = fromDatePicker.props;
  const {
    maxSelectableDate: maxToDate,
    minSelectableDate: minToDate,
    value: toValue
  } = toDatePicker.props;
  const fromDate = fromValue ? getValidConvertedDate(fromValue, dateFormatter) : null;
  const toDate = toValue ? getValidConvertedDate(toValue, dateFormatter) : null;
  const DPTo = /*#__PURE__*/React.cloneElement(toDatePicker, {
    initMinSelectableDate: minToDate,
    initMaxSelectableDate: maxToDate,
    minSelectableDate: getMinSelectableDate(fromDate),
    selectedDates: getNewSelectedDates(),
    isPartOfDateRangePicker: true
  });
  const DPFrom = /*#__PURE__*/React.cloneElement(fromDatePicker, {
    initMinSelectableDate: minFromDate,
    initMaxSelectableDate: maxFromDate,
    maxSelectableDate: getMaxSelectableDate(toDate),
    selectedDates: getNewSelectedDates(),
    isPartOfDateRangePicker: true
  });
  return _jsxs(Flexgrid, {
    columnGap: 3,
    children: [_jsx(FlexgridItem, {
      span: {
        xs: 12,
        sm: 6
      },
      children: DPFrom
    }), _jsx(FlexgridItem, {
      span: {
        xs: 12,
        sm: 6
      },
      children: DPTo
    })]
  });
}

DateRangePicker.propTypes = process.env.NODE_ENV !== "production" ? {
  /** The DatePicker component containing the 'From' date. */
  fromDatePicker: PropTypes.element.isRequired,

  /** The DatePicker component containing the 'To' date. */
  toDatePicker: PropTypes.element.isRequired
} : {};
DateRangePicker.displayName = "DateRangePicker";
export default DateRangePicker;